import IBloc from '../../base/IBloc';
import { 
    AdminManagementService, 
    AdminFilter, 
    AdminListResponse, 
    AdminWithRoles, 
    CreateAdminRequest, 
    UpdateAdminRequest 
} from '../../service/supabase/AdminManagementService';
import { Role } from '../../types/cms.types';

export interface AdminViewState {
    admins: AdminWithRoles[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    loading: boolean;
    error: string | null;
    selectedAdmin: AdminWithRoles | null;
    isFormOpen: boolean;
    isDeleteDialogOpen: boolean;
    adminToDelete: AdminWithRoles | null;
    filters: AdminFilter;
    availableRoles: Role[];
    isLoadingRoles: boolean;
    formType: 'create' | 'edit';
    hasData: boolean;
}

export class BlocManagementAdmins extends IBloc {
    /**
     * State chính của Admin Management
     */
    info: AdminViewState = {
        admins: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        selectedAdmin: null,
        isFormOpen: false,
        isDeleteDialogOpen: false,
        adminToDelete: null,
        filters: {},
        availableRoles: [],
        isLoadingRoles: false,
        formType: 'create',
        hasData: false
    };

    constructor() {
        super();
    }

    /**
     * Khởi tạo dữ liệu
     */
    async initialize() {
        await this.loadRoles();
        await this.loadAdmins();
        this.setStream('adminViewState', this.info);
    }

    /**
     * Load danh sách admins
     */
    async loadAdmins(page: number = 1, limit: number = 10) {
        this.info.loading = true;
        this.info.error = null;
        this.info.pagination.page = page;
        this.info.pagination.limit = limit;
        this.setStream('adminViewState', this.info);

        try {
            const response: AdminListResponse = await AdminManagementService.getAdmins(
                page,
                limit,
                this.info.filters
            );

            this.info.admins = response.data;
            this.info.pagination = {
                total: response.total,
                page: response.page,
                limit: response.limit,
                totalPages: response.totalPages
            };
            this.info.hasData = response.data.length > 0;
            this.info.loading = false;
            this.setStream('adminViewState', this.info);
        } catch (error) {
            console.error('Error loading admins:', error);
            this.info.loading = false;
            this.info.error = error instanceof Error ? error.message : 'Unknown error occurred';
            this.setStream('adminViewState', this.info);
        }
    }

    /**
     * Load danh sách roles
     */
    async loadRoles() {
        this.info.isLoadingRoles = true;
        this.setStream('adminViewState', this.info);

        try {
            const roles = await AdminManagementService.getAvailableRoles();
            this.info.availableRoles = roles;
            this.info.isLoadingRoles = false;
            this.setStream('adminViewState', this.info);
        } catch (error) {
            console.error('Error loading roles:', error);
            this.info.isLoadingRoles = false;
            this.info.error = error instanceof Error ? error.message : 'Failed to load roles';
            this.setStream('adminViewState', this.info);
        }
    }

    /**
     * Áp dụng bộ lọc
     */
    setFilters(filters: AdminFilter) {
        this.info.filters = filters;
        this.setStream('adminViewState', this.info);
        this.loadAdmins(1, this.info.pagination.limit); // Reset to first page when filtering
    }

    /**
     * Reset bộ lọc
     */
    resetFilters() {
        this.info.filters = {};
        this.setStream('adminViewState', this.info);
        this.loadAdmins(1, this.info.pagination.limit);
    }

    /**
     * Mở form tạo mới
     */
    openCreateForm() {
        this.info.isFormOpen = true;
        this.info.formType = 'create';
        this.info.selectedAdmin = null;
        this.setStream('adminViewState', this.info);
    }

    /**
     * Mở form chỉnh sửa
     */
    openEditForm(admin: AdminWithRoles) {
        this.info.isFormOpen = true;
        this.info.formType = 'edit';
        this.info.selectedAdmin = admin;
        this.setStream('adminViewState', this.info);
    }

    /**
     * Đóng form
     */
    closeForm() {
        this.info.isFormOpen = false;
        this.info.selectedAdmin = null;
        this.info.error = null;
        this.setStream('adminViewState', this.info);
    }

    /**
     * Tạo admin mới
     */
    async createAdmin(request: CreateAdminRequest) {
        this.info.loading = true;
        this.info.error = null;
        this.setStream('adminViewState', this.info);

        try {
            // Check username exists
            const usernameExists = await AdminManagementService.checkUsernameExists(request.user_name);
            if (usernameExists) {
                throw new Error('Tên đăng nhập đã tồn tại');
            }

            await AdminManagementService.createAdmin(request);
            this.info.loading = false;
            this.setStream('adminViewState', this.info);
            this.closeForm();
            // Reload current page
            this.loadAdmins(this.info.pagination.page, this.info.pagination.limit);
        } catch (error) {
            console.error('Error creating admin:', error);
            this.info.loading = false;
            this.info.error = error instanceof Error ? error.message : 'Failed to create admin';
            this.setStream('adminViewState', this.info);
        }
    }

    /**
     * Cập nhật admin
     */
    async updateAdmin(request: UpdateAdminRequest) {
        this.info.loading = true;
        this.info.error = null;
        this.setStream('adminViewState', this.info);

        try {
            // Check username exists (excluding current admin)
            const usernameExists = await AdminManagementService.checkUsernameExists(
                request.user_name, 
                request.id
            );
            if (usernameExists) {
                throw new Error('Tên đăng nhập đã tồn tại');
            }

            await AdminManagementService.updateAdmin(request);
            this.info.loading = false;
            this.setStream('adminViewState', this.info);
            this.closeForm();
            // Reload current page
            this.loadAdmins(this.info.pagination.page, this.info.pagination.limit);
        } catch (error) {
            console.error('Error updating admin:', error);
            this.info.loading = false;
            this.info.error = error instanceof Error ? error.message : 'Failed to update admin';
            this.setStream('adminViewState', this.info);
        }
    }

    /**
     * Mở dialog xóa
     */
    openDeleteDialog(admin: AdminWithRoles) {
        this.info.isDeleteDialogOpen = true;
        this.info.adminToDelete = admin;
        this.setStream('adminViewState', this.info);
    }

    /**
     * Đóng dialog xóa
     */
    closeDeleteDialog() {
        this.info.isDeleteDialogOpen = false;
        this.info.adminToDelete = null;
        this.setStream('adminViewState', this.info);
    }

    /**
     * Xóa admin
     */
    async deleteAdmin() {
        if (!this.info.adminToDelete?.id) return;

        this.info.loading = true;
        this.info.error = null;
        this.setStream('adminViewState', this.info);

        try {
            await AdminManagementService.deleteAdmin(this.info.adminToDelete.id);
            this.info.loading = false;
            this.setStream('adminViewState', this.info);
            this.closeDeleteDialog();
            
            // If we're on a page that becomes empty after deletion, go to previous page
            const totalAfterDeletion = this.info.pagination.total - 1;
            const maxPage = Math.ceil(totalAfterDeletion / this.info.pagination.limit);
            const targetPage = this.info.pagination.page > maxPage ? Math.max(1, maxPage) : this.info.pagination.page;
            
            this.loadAdmins(targetPage, this.info.pagination.limit);
        } catch (error) {
            console.error('Error deleting admin:', error);
            this.info.loading = false;
            this.info.error = error instanceof Error ? error.message : 'Failed to delete admin';
            this.setStream('adminViewState', this.info);
        }
    }

    /**
     * Chuyển trang
     */
    async goToPage(page: number) {
        if (page >= 1 && page <= this.info.pagination.totalPages) {
            await this.loadAdmins(page, this.info.pagination.limit);
        }
    }

    /**
     * Thay đổi số lượng item per page
     */
    async changePageSize(limit: number) {
        await this.loadAdmins(1, limit);
    }

    /**
     * Xóa lỗi
     */
    clearError() {
        this.info.error = null;
        this.setStream('adminViewState', this.info);
    }

    /**
     * Refresh dữ liệu
     */
    refreshAdmins() {
        this.loadAdmins(this.info.pagination.page, this.info.pagination.limit);
    }
}
